import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Trophy, Flame, Coins, Edit, Film, UserPlus, UserCheck, Gift, Trash2, Download, Camera, ImageIcon as Image2, Loader2 } from 'lucide-react';
import FollowListSheet from '../components/jixxeo/FollowListSheet';
import TipDialog from '../components/jixxeo/TipDialog';
import CreationLightbox from '../components/jixxeo/CreationLightbox';
import { usePwaInstall } from '../lib/usePwaInstall';

export default function ProfilePage() {
  const { user, setUser, refresh } = useAuth();
  const pwa = usePwaInstall();
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ display_name: '', bio: '', avatar_url: '' });
  const [followSheet, setFollowSheet] = useState(null); // 'followers' | 'following' | null
  const [tipOpen, setTipOpen] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const isSelf = !username || (user && user.username === username);
  const targetUsername = username || user?.username;

  const load = async () => {
    if (!targetUsername) return;
    const r = await api.get(`/users/${targetUsername}`);
    setProfile(r.data);
    if (user && !isSelf) {
      try { const s = await api.get(`/users/${targetUsername}/is_following`); setIsFollowing(!!s.data.following); } catch {}
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [targetUsername, user]);

  const startEdit = () => {
    setForm({ display_name: profile.user.display_name, bio: profile.user.bio || '', avatar_url: profile.user.avatar_url || '' });
    setEditing(true);
  };
  const save = async () => {
    try {
      const { data } = await api.put('/profile', form);
      setUser(data); toast.success('Saved'); setEditing(false); refresh(); load();
    } catch { toast.error('Failed'); }
  };
  const follow = async () => {
    try {
      const { data } = await api.post('/follow', { target_user_id: profile.user.id });
      setIsFollowing(data.following);
      await load();
    } catch { toast.error('Follow failed'); }
  };
  const deleteCreation = async (cid) => {
    if (!window.confirm('Delete this creation?')) return;
    try { await api.delete(`/creations/mine/${cid}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const uploadFile = async (file, kind) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5 MB'); return; }
    const setter = kind === 'avatar' ? setUploadingAvatar : setUploadingCover;
    setter(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/upload/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(kind === 'avatar' ? 'Profile picture updated' : 'Cover photo updated');
      refresh();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setter(false); }
  };

  if (!profile) return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-shell">
      {/* Skeleton — appears instantly so the page never looks frozen on "Loading…" */}
      <div className="glass rounded-2xl overflow-hidden">
        <div className="h-32 sm:h-44 bg-gradient-to-br from-violet-700/30 via-fuchsia-500/20 to-cyan-500/20 animate-pulse" />
        <div className="p-6 pt-0 -mt-10 space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 rounded-full bg-white/10 ring-4 ring-slate-950 animate-pulse flex-shrink-0" />
            <div className="flex-1 pt-10 space-y-2">
              <div className="h-5 w-40 bg-white/10 rounded animate-pulse" />
              <div className="h-3 w-24 bg-white/10 rounded animate-pulse" />
              <div className="flex gap-5 pt-1">
                <div className="h-3 w-16 bg-white/10 rounded animate-pulse" />
                <div className="h-3 w-16 bg-white/10 rounded animate-pulse" />
                <div className="h-3 w-16 bg-white/10 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="aspect-square rounded-xl bg-white/5 animate-pulse" />
        ))}
      </div>
    </div>
  );
  const p = profile.user;
  const initials = (p.display_name || p.username || 'JX').slice(0, 2).toUpperCase();

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Card className="glass p-0 overflow-hidden">
        {/* Cover photo */}
        <div className="relative h-32 sm:h-44 bg-gradient-to-br from-violet-600/40 via-fuchsia-500/30 to-cyan-500/30 overflow-hidden">
          {p.cover_url && <img src={p.cover_url} alt="" className="w-full h-full object-cover" />}
          {isSelf && (
            <label data-testid="upload-cover-btn"
                   className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/60 border border-white/20 text-xs font-semibold text-white backdrop-blur hover:bg-black/80 cursor-pointer">
              {uploadingCover ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Image2 className="w-3.5 h-3.5" />}
              {uploadingCover ? 'Uploading…' : 'Change cover'}
              <input data-testid="upload-cover-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
                     onChange={e => uploadFile(e.target.files?.[0], 'cover')} />
            </label>
          )}
        </div>

        <div className="p-6 pt-0 -mt-10">
          <div className="flex items-start gap-4">
            <div className="relative flex-shrink-0">
              <Avatar className="w-20 h-20 ring-4 ring-slate-950">
                <AvatarImage src={p.avatar_url} />
                <AvatarFallback className="gradient-brand text-white text-2xl font-black">{initials}</AvatarFallback>
              </Avatar>
              {isSelf && (
                <label data-testid="upload-avatar-btn"
                       className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full gradient-brand flex items-center justify-center shadow-lg cursor-pointer hover:scale-105 transition-transform">
                  {uploadingAvatar ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Camera className="w-4 h-4 text-white" />}
                  <input data-testid="upload-avatar-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
                         onChange={e => uploadFile(e.target.files?.[0], 'avatar')} />
                </label>
              )}
            </div>
            <div className="flex-1 min-w-0 pt-10">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-2xl font-bold truncate" data-testid="profile-display">{p.display_name}</h1>
                {p.is_premium && <span className="text-xs px-2 py-0.5 rounded gradient-gold text-slate-950 font-bold flex-shrink-0">PREMIUM</span>}
              </div>
              <div className="text-slate-400 text-sm">@{p.username}</div>
              {p.bio && <p className="text-sm mt-2 break-words">{p.bio}</p>}
              <div className="flex gap-5 mt-3 text-sm flex-wrap">
                <button data-testid="profile-followers-count" onClick={() => setFollowSheet('followers')} className="hover:text-violet-300 transition-colors">
                  <span className="font-bold">{profile.followers}</span> <span className="text-slate-400">followers</span>
                </button>
                <button data-testid="profile-following-count" onClick={() => setFollowSheet('following')} className="hover:text-violet-300 transition-colors">
                  <span className="font-bold">{profile.following}</span> <span className="text-slate-400">following</span>
                </button>
                <div><span className="font-bold">{profile.creations?.length || 0}</span> <span className="text-slate-400">creations</span></div>
              </div>
            </div>
          </div>
          {/* Dedicated action row so Follow/Tip never overlap the name/bio */}
          <div className="flex gap-2 flex-wrap mt-4">
            {isSelf ? (
              <Button data-testid="edit-profile" onClick={startEdit} variant="outline" size="sm"><Edit className="w-3 h-3 mr-1" /> Edit profile</Button>
            ) : user && (
              <>
                <Button data-testid="follow-btn" onClick={follow} size="sm" className={isFollowing ? 'bg-white/10' : 'gradient-brand text-white'}>
                  {isFollowing ? <><UserCheck className="w-3 h-3 mr-1" /> Following</> : <><UserPlus className="w-3 h-3 mr-1" /> Follow</>}
                </Button>
                <Button data-testid="tip-btn" onClick={() => setTipOpen(true)} size="sm" variant="outline" className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
                  <Gift className="w-3 h-3 mr-1" /> Tip
                </Button>
              </>
            )}
          </div>
          {isSelf && (
            <div className="grid grid-cols-3 gap-2 mt-5">
              <div className="glass rounded-xl p-3 text-center"><Coins className="w-4 h-4 text-amber-400 mx-auto" /><div className="font-bold mt-1">{p.jxp}</div><div className="text-xs text-slate-400">JXP</div></div>
              <div className="glass rounded-xl p-3 text-center"><Flame className="w-4 h-4 text-orange-400 mx-auto" /><div className="font-bold mt-1">{p.streak}</div><div className="text-xs text-slate-400">Streak</div></div>
              <div className="glass rounded-xl p-3 text-center"><Trophy className="w-4 h-4 text-violet-400 mx-auto" /><div className="font-bold mt-1">{p.achievements?.length || 0}</div><div className="text-xs text-slate-400">Achievements</div></div>
            </div>
          )}
        </div>
      </Card>

      {isSelf && !pwa.installed && (
        <Card data-testid="profile-install-card" className="glass p-4 border-violet-500/40 bg-gradient-to-br from-violet-500/15 to-cyan-500/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-brand flex items-center justify-center flex-shrink-0">
              <Download className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold">Install Jixxeo AI</div>
              <div className="text-xs text-slate-400">Add Jixxeo AI to your phone for faster access.</div>
            </div>
            <Button
              data-testid="profile-install-btn"
              size="sm"
              onClick={async () => {
                if (pwa.installable) {
                  const r = await pwa.promptInstall();
                  if (r.outcome === 'unavailable') window.location.assign('/install');
                } else {
                  window.location.assign('/install');
                }
              }}
              className="gradient-brand text-white"
            >Install</Button>
          </div>
        </Card>
      )}

      {isSelf && (
        <Card className="glass p-4">
          <Link to="/recordings" data-testid="profile-my-recordings" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center"><Film className="w-5 h-5 text-violet-300" /></div>
            <div className="flex-1"><div className="font-semibold">My Recordings</div><div className="text-xs text-slate-400">Your saved Jixxeo AI recordings</div></div>
            <span className="text-violet-400 group-hover:translate-x-1 transition-transform">→</span>
          </Link>
        </Card>
      )}

      {editing && (
        <Card className="glass p-4 space-y-3">
          <Label>Display name</Label>
          <Input data-testid="edit-display" value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} />
          <Label>Avatar URL</Label>
          <Input data-testid="edit-avatar" value={form.avatar_url} onChange={e => setForm({ ...form, avatar_url: e.target.value })} placeholder="https://…" />
          <Label>Bio</Label>
          <Textarea data-testid="edit-bio" value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} />
          <div className="flex gap-2">
            <Button data-testid="save-profile" onClick={save} className="gradient-brand text-white">Save</Button>
            <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      <div>
        <h2 className="font-display text-xl font-bold mb-3">Creations</h2>
        {(profile.creations || []).length === 0 && <Card className="glass p-8 text-center text-slate-400 text-sm">No public creations yet</Card>}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {(profile.creations || []).map((c, i) => {
            const isVideo = c.type === 'video';
            // Videos need their poster for an <img> tile; images use their url directly.
            // onError falls back to the generic Jixxeo poster so no tile ever shows a broken box.
            const thumbSrc = isVideo ? (c.poster_url || '/api/videos/poster/_missing_.jpg') : c.url;
            return (
              <div key={c.id} className="glass rounded-xl overflow-hidden aspect-square relative group">
                <button
                  data-testid={`creation-tile-${c.id}`}
                  onClick={() => setLightboxIdx(i)}
                  className="absolute inset-0 w-full h-full focus:outline-none focus:ring-2 focus:ring-violet-400/60"
                  aria-label="Open preview"
                >
                  {thumbSrc && (
                    <img
                      src={thumbSrc}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback chain: if the primary thumb 404s, retry with the server-side
                        // backfill endpoint (which 302s to a safe default poster).
                        const img = e.currentTarget;
                        if (img.dataset.fallbackApplied) return;
                        img.dataset.fallbackApplied = '1';
                        img.src = `/api/videos/poster/${c.id}.jpg`;
                      }}
                    />
                  )}
                  {isVideo && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-10 h-10 rounded-full bg-black/55 border border-white/25 flex items-center justify-center backdrop-blur-sm">
                        <Film className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  )}
                </button>
                {isSelf && (
                  <button
                    data-testid={`delete-creation-${c.id}`}
                    onClick={(e) => { e.stopPropagation(); deleteCreation(c.id); }}
                    className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-rose-600/95 text-white sm:opacity-0 sm:group-hover:opacity-100 flex items-center justify-center shadow-lg active:scale-95 transition-transform"
                    aria-label="Delete creation"
                    title="Delete"
                  ><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {lightboxIdx !== null && (profile.creations || []).length > 0 && (
        <CreationLightbox
          items={(profile.creations || []).map(c => ({
            id: c.id,
            type: c.type || 'image',
            url: c.url,
            prompt: c.prompt,
            poster_url: c.poster_url,
            likes: c.likes || 0,
            published: true,
            creator: {
              id: p.id, username: p.username,
              display_name: p.display_name, avatar_url: p.avatar_url,
            },
          }))}
          startIndex={lightboxIdx}
          showOwnerActions={isSelf}
          onClose={() => setLightboxIdx(null)}
        />
      )}

      <FollowListSheet open={!!followSheet} onOpenChange={(o) => !o && setFollowSheet(null)} username={targetUsername} kind={followSheet || 'followers'} />
      <TipDialog open={tipOpen} onOpenChange={setTipOpen} targetUsername={targetUsername} onTipped={load} />
    </div>
  );
}
